import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, CheckCircle, AlertCircle, Code } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface ExamSettings {
  id: number;
  answer_mode: 'upload' | 'online';
  updated_at: string | null;
  updated_by: string | null;
}

export default function AnswerSettings() {
  const [settings, setSettings] = useState<ExamSettings>({
    id: 1,
    answer_mode: 'online',
    updated_at: null,
    updated_by: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 加载当前设置
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('exam_settings')
        .select('id, answer_mode, updated_at, updated_by')
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        return;
      }

      if (data) {
        setSettings({
          id: data.id,
          answer_mode: data.answer_mode as 'upload' | 'online',
          updated_at: data.updated_at,
          updated_by: data.updated_by,
        });
      }
    } catch (err) {
      // 静默处理错误
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const { data, error } = await supabase
        .from('exam_settings')
        .update({
          answer_mode: settings.answer_mode,
          updated_at: new Date().toISOString(),
        })
        .eq('id', settings.id)
        .select();

      if (error) {
        setMessage({ type: 'error', text: '保存失败: ' + error.message });
        return;
      }

      if (!data || data.length === 0) {
        setMessage({ type: 'error', text: '保存失败：可能被权限策略拦截' });
        return;
      }

      setMessage({ type: 'success', text: '设置已保存' });
      // 3秒后清除消息
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: '保存异常，请重试' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">答题设置</h1>
          </div>
          <p className="text-gray-500 ml-13">配置答题方式，影响编程题、Scratch编程题的学员端答题界面</p>
        </div>

        {/* 消息提示 */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </motion.div>
        )}

        {/* 非客观题答题方式设置 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Code className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">非客观题答题方式设置</h2>
            </div>
            <p className="text-gray-500 text-sm mt-2 ml-11">该设置将决定学员端答题界面的展示形式</p>
          </div>

          <div className="p-6 space-y-4">
            {/* 上传文件选项 */}
            <div
              onClick={() => setSettings({ ...settings, answer_mode: 'upload' })}
              className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.answer_mode === 'upload'
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    settings.answer_mode === 'upload'
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-gray-300'
                  }`}
                >
                  {settings.answer_mode === 'upload' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">上传文件</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                      学员上传代码文件/截图
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">
                    学员上传代码文件(.py/.cpp)或截图(.png/.jpg)，适用于线下编写后提交的场景
                  </p>
                </div>
              </div>
            </div>

            {/* 在线答题选项 */}
            <div
              onClick={() => setSettings({ ...settings, answer_mode: 'online' })}
              className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.answer_mode === 'online'
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    settings.answer_mode === 'online'
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-gray-300'
                  }`}
                >
                  {settings.answer_mode === 'online' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">在线答题</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                      在系统内嵌编辑器中直接编写代码
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">
                    学员在系统内嵌的代码编辑器中直接编写代码，支持实时运行和调试
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 说明信息 */}
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-100 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-blue-600 text-xs font-bold">i</span>
            </div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">说明：</p>
              <ul className="space-y-1 text-blue-700">
                <li>• 答题方式为全局设置，影响所有试卷的刷题平台答题行为</li>
                <li>• 我的作业：学员始终可以作答</li>
                <li>• 答题方式设置对新创建的试卷生效，已发布的试卷不受此设置影响</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="flex justify-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-200"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>保存中...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>保存设置</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
